import {
  GroupTextField,
  GroupTextFieldRow,
  Button,
  Input,
  Icon,
} from "@elaniintc/goes-design-system";
import type { VariableEntry } from "@/schemas/workflow/notification-node";
import type { NodeVariable } from "../../types";
import { useTemplateVariableInput } from "../hooks/useTemplateVariableInput";
import { VariableSelector } from "./variable-selector";
import { useMaskito } from "@maskito/react";
import { maskitoDateOptionsGenerator } from "@maskito/kit";

interface TemplateVariableInputProps {
  variableName: string;
  variableType: string;
  required: boolean;
  description?: string;
  variables: NodeVariable[];
  elements: VariableEntry[];
  onChange: (elements: VariableEntry[]) => void;
}

export const TemplateVariableInput: React.FC<TemplateVariableInputProps> = ({
  variableName,
  variableType,
  required,
  description,
  variables,
  elements,
  onChange,
}) => {
  const { updateElement, removeElement, toggleElementType } =
    useTemplateVariableInput({ elements, onChange });

  // Maskito para fechas
  const dateMask = useMaskito({
    options: maskitoDateOptionsGenerator({
      mode: "dd/mm/yyyy",
      separator: "/",
    }),
  });

  // Configuración según el tipo de variable
  const getInputConfig = () => {
    switch (variableType) {
      case "string":
      case "text":
        return {
          placeholder: "Escribe un texto",
          maxLength: 255,
          validate: () => true,
          inputType: "text" as const,
          mask: undefined,
          icon: "text" as const,
        };
      case "float":
        return {
          placeholder: "0.0",
          maxLength: 20,
          validate: (value: string) => /^\d*\.?\d{0,4}$/.test(value),
          inputType: "text" as const,
          mask: undefined,
          icon: "hashtag" as const,
        };
      case "number":
      case "integer":
        return {
          placeholder: "0",
          maxLength: 20,
          validate: (value: string) => /^\d*$/.test(value),
          inputType: "text" as const,
          mask: undefined,
          icon: "hashtag" as const,
        };
      case "date":
        return {
          placeholder: "DD/MM/YYYY",
          maxLength: 10,
          validate: () => true,
          inputType: "text" as const,
          mask: dateMask,
          icon: "calendar" as const,
        };
      default:
        return {
          placeholder: "Escribe un valor",
          maxLength: 255,
          validate: () => true,
          inputType: "text" as const,
          mask: undefined,
          icon: "text" as const,
        };
    }
  };

  const config = getInputConfig();

  return (
    <div className="flex flex-col gap-2">
      <label className="font-semibold text-body-loud text-sm leading-2xl">
        {variableName}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {description && (
        <span className="text-body-quiet text-xs leading-xl font-normal -mt-1">
          {description}
        </span>
      )}

      <div className="space-y-2">
        {elements.map((element, index) => {
          return (
            <div key={index} className="flex items-center gap-2">
              {element.type === "var" ? (
                <GroupTextField className="flex-1" label="">
                  <GroupTextFieldRow className="overflow-x-hidden">
                    <Button
                      variant="quiet"
                      onClick={() => toggleElementType(String(index))}
                    >
                      {element.type}
                    </Button>
                    <VariableSelector
                      variables={variables}
                      selectedData={{
                        value: element.value,
                        connection: element.connection,
                        field: element.field,
                        global_type: element.global_type,
                        output_name: element.output_name || "",
                      }}
                      type={element.var_type}
                      onUpdateElement={(data) =>
                        updateElement(String(index), data)
                      }
                    />
                  </GroupTextFieldRow>
                </GroupTextField>
              ) : (
                <GroupTextField className="flex-1" label="">
                  <GroupTextFieldRow className="overflow-x-hidden">
                    <Button
                      variant="quiet"
                      onClick={() => toggleElementType(String(index))}
                    >
                      {element.type}
                    </Button>
                    <div className="relative w-full">
                      <Input
                        group="right"
                        className="w-full pl-9"
                        placeholder={config.placeholder}
                        maxLength={config.maxLength}
                        value={element.value}
                        type={config.inputType}
                        ref={variableType === "date" ? config.mask : undefined}
                        onChange={(e) => {
                          if (!config.validate(e.target.value)) return;
                          updateElement(String(index), e.target.value);
                        }}
                      />
                      <Icon
                        name={config.icon}
                        type="far"
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body"
                      />
                    </div>
                  </GroupTextFieldRow>
                </GroupTextField>
              )}
              {index > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeElement(String(index))}
                  aria-label="Eliminar"
                >
                  <Icon name="times" type="far" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
